// src/context/AuthContext.jsx
/**
 * AuthContext — global authentication state for the app.
 *
 * Exposes via useAuth():
 *   isLoggedIn  null | boolean  — null = still checking, false = logged out, true = logged in
 *   user        object | null   — { id, email, username, is_verified, is_active, is_admin }
 *   logout()                    — POST /auth/logout, clear cookies, reset state
 *   setUser()                   — update user object (used by Account page after profile changes)
 *   setIsLoggedIn()             — update login state directly
 *   applyTheme(dark)            — set data-theme on <html> element for CSS dark/light mode
 *
 * Lifecycle:
 *   On mount:  GET /auth/me → if 200, set user + fetch settings + apply theme → isLoggedIn=true
 *              Skips the /auth/me call on public routes to avoid unnecessary 401s.
 *   settings-changed event: re-fetch /settings/ and re-apply theme (Admin page broadcasts this)
 *   On logout: POST /auth/logout, reset state, clear any non-HttpOnly cookies.
 *
 * Registers logout handler in logoutHandler.js so api.js can trigger logout on
 * refresh failure without a circular import.
 */
import React, { createContext, useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import api from "../api/api";
import { setAuthContext } from "../utils/logoutHandler";

export const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(null); // null = unknown
  const [user, setUser] = useState(null);

  const logout = async (msg = null) => {
    try {
      await api.post("/auth/logout"); // clear cookies on backend
    } catch (err) {
      console.warn("Logout API failed (may already be expired).");
    }

    setIsLoggedIn(false);
    setUser(null);

    // Just in case: remove any non-HttpOnly cookies // NOSONAR
    document.cookie.split(";").forEach((c) => { // NOSONAR
      document.cookie = c // NOSONAR
        .replace(/^ +/, "")
        .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
    });

    if (msg) alert(msg);
  };

  // Register logout handler so api.js interceptor can trigger logout
  useEffect(() => {
    setAuthContext({ logout });
  }, []); // eslint-disable-line

  const applyTheme = (dark) => {
    document.documentElement.setAttribute("data-theme", dark ? "dark" : "light");
  };

  useEffect(() => {
    const publicRoutes = ["/login", "/register", "/verify-success", "/verify-error", "/resend-verify", "/forgot-password"];
    const publicPrefixes = ["/card-view/", "/card-label/"];
    const path = window.location.pathname;
    if (publicRoutes.includes(path) || publicPrefixes.some((p) => path.startsWith(p))) {
      setIsLoggedIn(false);
      return; // ✅ Don’t trigger /auth/me on public routes
    }

    api.get("/auth/me")
      .then(async (res) => {
        setUser(res.data);
        // Apply theme BEFORE rendering children so there's no light-mode flash
        try {
          const sr = await api.get("/settings/");
          applyTheme(sr.data.dark_mode);
        } catch (_) { console.warn("Settings fetch failed on init"); }
        setIsLoggedIn(true);
      })
      .catch(() => {
        setUser(null);
        setIsLoggedIn(false);
      });
  }, []);

  // Re-apply theme when settings are updated from any page
  useEffect(() => {
    const handler = () => {
      api.get("/settings/")
        .then((sr) => applyTheme(sr.data.dark_mode))
        .catch(() => console.warn("Settings reload failed"));
    };
    window.addEventListener("settings-changed", handler);
    return () => window.removeEventListener("settings-changed", handler);
  }, []);

  // If we’re still checking, don’t render children yet
  if (isLoggedIn === null) {
    return <p>Loading session...</p>;
  }

  return (
    <AuthContext.Provider value={{ isLoggedIn, user, logout, setUser, setIsLoggedIn, applyTheme }}>
      {children}
    </AuthContext.Provider>
  );
};
AuthProvider.propTypes = { children: PropTypes.node.isRequired };
